import { useCallback } from "react";
import { useMedia } from "../hooks/api/media/useMedia";

// Wrap a raw Blob (or unnamed File) into a File with a usable name + type so
// the /media endpoint's `content_type` + `file_name` validation passes.
// Returns the input unchanged when it's already a named File.
function ensureNamedFile(input, prefix) {
  if (input instanceof File && input.name) return input;
  const type = input?.type || "audio/webm";
  // Pull "webm" out of "audio/webm;codecs=opus".
  const ext = (type.split("/")[1] || "webm").split(";")[0] || "webm";
  return new File([input], `${prefix}-${Date.now()}.${ext}`, { type });
}

/**
 * Hook that provides file upload functions backed by the Media API.
 */
export function useStorageUpload(context = "workspace") {
  const { uploadMedia, uploading } = useMedia(context);

  const uploadFile = useCallback(
    async (file, workspaceId) => {
      const response = await uploadMedia(file, workspaceId);
      return { path: response.key, preview_url: response.media_item.url, media_item: response.media_item };
    },
    [uploadMedia]
  );

  const uploadVoiceNote = useCallback(
    async (file, onProgress) => {
      if (onProgress) onProgress(0);
      // Callers pass raw Blobs (MediaRecorder output, or user-picked Files
      // re-wrapped as Blobs) — wrap into a named File so the upload has a
      // file_name and content_type.
      const namedFile = ensureNamedFile(file, "voice-note");
      const response = await uploadMedia(namedFile);
      if (onProgress) onProgress(100);
      return {
        signed_url: response.media_item.url,
        file_name: response.media_item.file_name,
        object_key: response.key,
        folder_name: null,
        storage_type: "media_api",
        media_item: response.media_item,
      };
    },
    [uploadMedia]
  );

  const uploadPublicVoiceNote = useCallback(
    async (file, upload, onProgress) => {
      // Public voice notes are uploaded by unauthenticated visitors,
      // so /media is not an option (auth-required). The caller fetches
      // a presigned POST payload from /public/notes/:id/v2/signed_upload_url
      // and we POST the file straight to storage with those fields.
      if (!upload?.url || !upload?.fields || !upload?.key) {
        throw new Error("uploadPublicVoiceNote requires v2 upload metadata");
      }
      return new Promise((resolve, reject) => {
        const formData = new FormData();
        Object.entries(upload.fields).forEach(([k, v]) => formData.append(k, v));
        formData.append("file", file);

        const xhr = new XMLHttpRequest();
        xhr.open("POST", upload.url);
        if (onProgress) {
          xhr.upload.onprogress = (e) => {
            if (e.lengthComputable) {
              onProgress(Math.round((e.loaded / e.total) * 100));
            }
          };
        }
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve({
              object_key: upload.key,
              file_name: file.name,
            });
          } else {
            reject(new Error(`Upload failed: ${xhr.status} ${xhr.statusText}`));
          }
        };
        xhr.onerror = () => reject(new Error("Upload failed"));
        xhr.send(formData);
      });
    },
    []
  );

  const uploadFont = useCallback(
    async (file, fontName, fontFormat) => {
      const response = await uploadMedia(file);
      const formattedFontName = fontName
        .toLowerCase()
        .replace(/\s+/g, "-")
        .replace(/[^a-z0-9-]/g, "");
      return {
        fontFamily: formattedFontName,
        url: response.media_item.url,
        format: fontFormat,
        media_item: response.media_item,
      };
    },
    [uploadMedia]
  );

  const uploadFeedbackScreenshot = useCallback(
    async (file) => {
      const response = await uploadMedia(file);
      return { path: response.media_item.url, media_item: response.media_item };
    },
    [uploadMedia]
  );

  return {
    uploadFile,
    uploadVoiceNote,
    uploadPublicVoiceNote,
    uploadFont,
    uploadFeedbackScreenshot,
    uploading,
  };
}
